import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { useFormikContext, getIn } from 'formik';
import { lookupsJson } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

import Field from 'core/components/form/form.v2';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import { AppliedTag } from 'common/components/farm/applied-tag';
import useStyles from '../liability-coverages-styles';

// Filter out the "No coverage" option (id: 0) from man months dropdown
const manMonthsOptions = lookupsJson.farm.manMonthsOptions.filter((opt) => opt.id !== 0);

const EmployersLiability = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values } = useFormikContext();

  // "Total man months (per year)" is a Forreston-only rate-structure input; CFM does not use it
  // and hides it via this brand flag (#33964).
  const hideManMonths = appConfig?.featureFlags?.hideFarmManMonths;

  const employees = getIn(values, 'farmLiabilityCoverage.employees') || {};
  const numManMonths = getIn(values, 'farmLiabilityCoverage.numManMonths') || 0;
  const excludedEmployeesDescription =
    getIn(values, 'farmLiabilityCoverage.exclusions.excludedEmployeesDescription') || '';
  const initialOpen =
    numManMonths > 0 ||
    employees.fullTime > 0 ||
    employees.partTime > 0 ||
    employees.partTime40DaysOrLess > 0 ||
    !!excludedEmployeesDescription;

  return (
    <div className={classes.sectionContainer} id="farmLiabilityCoverage.employersLiability">
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Employer's liability - farm employees</div>}
        contentRight={initialOpen ? <AppliedTag /> : null}
        initialOpen={initialOpen}
      >
        <Grid container spacing={2}>
          {/* Labels forced to shrink so long day-range text doesn't overlap the
              numeric value (#30499.1). Day-range detail lives in the tooltip on
              each field - the visible labels are kept short so the three
              xs={4} columns don't crash into each other (#30632). */}
          <Field
            xs={4}
            type="numeric"
            id="farmLiabilityCoverage.employees.fullTime"
            name="farmLiabilityCoverage.employees.fullTime"
            mode="dark"
            label="Number of Full-Time Employees"
            labelProps={{ shrink: true }}
            tooltipText="Total number of full time farm employees working 180 days per year or more."
            disabled={disabled}
            onChange={onChangeActive}
          />
          <Field
            xs={4}
            type="numeric"
            id="farmLiabilityCoverage.employees.partTime"
            name="farmLiabilityCoverage.employees.partTime"
            mode="dark"
            label="Number of Part-Time Employees"
            labelProps={{ shrink: true }}
            tooltipText="Total number of part time farm employees working over 40 days, but less than 180 days per year."
            disabled={disabled}
            onChange={onChangeActive}
          />
          <Field
            xs={4}
            type="numeric"
            id="farmLiabilityCoverage.employees.partTime40DaysOrLess"
            name="farmLiabilityCoverage.employees.partTime40DaysOrLess"
            mode="dark"
            label="Total Part-Time Man-Days"
            labelProps={{ shrink: true }}
            tooltipText="Total number of man-days for part time farm employees working 40 days or less per year."
            disabled={disabled}
            onChange={onChangeActive}
          />
          {!hideManMonths && (
            <Field
              xs={12}
              type="select"
              id="farmLiabilityCoverage.numManMonths"
              name="farmLiabilityCoverage.numManMonths"
              mode="dark"
              label="Total man months (per year)"
              options={manMonthsOptions}
              disabled={disabled}
              onChange={onChangeActive}
            />
          )}
          <Field
            xs={12}
            type="textarea"
            id="farmLiabilityCoverage.exclusions.excludedEmployeesDescription"
            name="farmLiabilityCoverage.exclusions.excludedEmployeesDescription"
            mode="dark"
            label="Farm Employees Excluded from Coverage"
            tooltipText="Describe any farm employees excluded from employer's liability coverage."
            disabled={disabled}
            onChange={onChangeActive}
          />
        </Grid>
      </SubRiskCollapsibleSection>
    </div>
  );
};

EmployersLiability.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default EmployersLiability;
